import { Router } from '@angular/router';
import { Component, Input, OnInit } from '@angular/core';
import { ServiceProvider } from '@gms/tsp-api';
import { select, Store } from '@ngrx/store';
import { VIEW_ALL_TSP } from 'app/store/header/header.reducers';
import {
  fetchInfoPostTspInfo,
  setSelectedInfoPostTSP,
  resetRedirectedTSPID,
} from 'app/store/infopost/infopost.actions';
import {
  getRedirectedTSPID,
  getSelectedInfoPostTSP,
  selectAllInfoPostTSP,
} from 'app/store/infopost/infopost.selectors';
import { IInfoPostState } from 'app/store/infopost/infopost.state';
import isEqual from 'lodash/isEqual';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';
import { IObjectWithLabel } from 'shared/components/combo-button/combo-button.component';
import { HasSubscriptions } from 'shared/components/higher-order/has-subscriptions';
import { isNullOrUndefined } from 'shared/utils/type.utils';
import { IAppState } from 'app/store/app/app.state';
import { SetTsp } from 'app/store/header/header.actions';

const ALL_TSP_OPTION = 'View All TSPs';
const PLACEHOLDER = 'Select a TSP';

@Component({
  selector: 'app-infopost-tsp-selector',
  templateUrl: './infopost-tsp-selector.component.html',
})
export class InfopostTspSelectorComponent extends HasSubscriptions implements OnInit {
  /**
   * Set to false to disable View All
   */
  @Input() canViewAll = false;

  selectTsps$: Observable<any> = this._store.pipe(
    select(selectAllInfoPostTSP),
    filter((tsps: ServiceProvider[]) => Boolean(tsps && tsps.length))
  );

  options$: Observable<IObjectWithLabel<ServiceProvider>[]> = this.selectTsps$.pipe(
    map((data) => {
      let serviceProvider = data.map(
        (item: ServiceProvider): IObjectWithLabel<ServiceProvider> => ({
          fullObject: item,
          label: this.getLabel(item),
        })
      );
      if (this.canViewAll) {
        serviceProvider.push(this.SHOW_ALL_OPTION);
      }

      return serviceProvider;
    })
  );

  selectedTsp$: Observable<ServiceProvider> = this._store.pipe(select(getSelectedInfoPostTSP));

  SHOW_ALL_OPTION = { fullObject: VIEW_ALL_TSP, label: ALL_TSP_OPTION };
  label = this.canViewAll ? this.SHOW_ALL_OPTION.label : PLACEHOLDER;
  redirectedTSPID: number;

  constructor(private _store: Store<IInfoPostState>, private store: Store<IAppState>, private _router:Router) {
    super();
  }

  ngOnInit(): void {
    this.addSubscription(
      this._store
      .select(selectAllInfoPostTSP)
      .pipe(
        take(1),
        map((tspData) => !!tspData && tspData.length > 0),
        tap((isTspDataAvailable) => {
          if (!isTspDataAvailable) {
            this._store.dispatch(fetchInfoPostTspInfo());
          }
        })
      )
      .subscribe()
    );
    this.addSubscription(
      combineLatest([this.options$, this.selectedTsp$])
      .pipe(distinctUntilChanged(isEqual))
      .subscribe(([tspOptions, selectedTsp]: [Array<IObjectWithLabel>, ServiceProvider]) => {
        if (this.isTspAvailable(tspOptions, selectedTsp)) {
          this.label = tspOptions.find(
            (tsp) => (tsp.fullObject as ServiceProvider).providerId === selectedTsp.providerId
          ).label;
        } else {
          // If user only has one option in the dropdown, auto-select it when not given an available TSP to select
          if (tspOptions.length === 1) {
            this.selectFirstAvailableTsp(tspOptions);
          } else {
            this.clearSelection();
          }
        }
      })
    );
    this.addSubscription(
      this._store
      .select(getRedirectedTSPID)
      .pipe(
        map((tspId) => tspId),
        tap((tspId) => {
          if (tspId) {
            this.redirectedTSPID = tspId;
            this.options$.subscribe((tspOptions=>{
            let option = tspOptions.find((tsp) => (tsp.fullObject as ServiceProvider).providerId === tspId).fullObject;
            this.optionSelected(option, false);
            this._store.dispatch(resetRedirectedTSPID());
            }))
          }
        })
      )
      .subscribe()
    );
  }

  optionSelected(tsp: ServiceProvider, isResetTspUrl: boolean = true) {
    const noSelectionDefault = this.canViewAll ? this.SHOW_ALL_OPTION.label : PLACEHOLDER;
    this.label = tsp && tsp.providerId ? this.getLabel(tsp) : noSelectionDefault;
    this._store.dispatch(setSelectedInfoPostTSP({ tspServiceRequester: tsp }));
    this.store.dispatch(new SetTsp({ tsp }));

    if(isResetTspUrl && this.redirectedTSPID) {
      this._router.navigate([], { queryParams: { tspId: null }, queryParamsHandling: 'merge' });
      this.redirectedTSPID = null;
    }
  }

  private selectFirstAvailableTsp(availableOptions: IObjectWithLabel<ServiceProvider>[]) {
    const firstAvailableTsp = availableOptions[0];

    this.label = firstAvailableTsp.label;
    this._store.dispatch(
      setSelectedInfoPostTSP({
        tspServiceRequester: firstAvailableTsp.fullObject,
        overrideIfAlreadySet: true,
      })
    );
  }

  private isTspAvailable(
    availableOptions: IObjectWithLabel<ServiceProvider>[],
    tspToTest: ServiceProvider
  ) {
    if (isNullOrUndefined(tspToTest) || !tspToTest.providerId) {
      return false;
    }
    return availableOptions.some((tsp) => tsp.fullObject.providerId === tspToTest.providerId);
  }

  private clearSelection() {
    const noSelectionValue = this.canViewAll ? this.SHOW_ALL_OPTION.label : PLACEHOLDER;
    this.label = noSelectionValue;

    this._store.dispatch(
      setSelectedInfoPostTSP({ tspServiceRequester: VIEW_ALL_TSP, overrideIfAlreadySet: true })
    );
  }

  getLabel(tsp: ServiceProvider) {
    return `${tsp.dunsNumber} - ${tsp.legalName}`;
  }
}
